/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICheckoutPaymentInstrumentViewProps, ICheckoutPaymentOverlayModal } from './lds-checkout-payment-instrument';

import { getIdentificationTypesAsync, getIdentificationTypesApplicabilityRulesAsync } from '../../actions/DataActionExtension.g';
// @ts-ignore
import { Secondary } from '@churchofjesuschrist/eden-buttons';
// @ts-ignore
import { Error } from '@churchofjesuschrist/eden-alert';

const OverlayModal: React.FC<ICheckoutPaymentOverlayModal> = ({ modal }) => {
    return <Node {...modal} />;
};

const CheckoutPaymentInstrumentView: React.FC<ICheckoutPaymentInstrumentViewProps> = props => {
    const {
        checkoutPaymentInstrument,
        // className,
        // id,
        waiting,
        alert,
        paymentInformation,
        addPaymentForm,
        overlayModal,
        updateFunction,
        context,
        resources,
        updatePaymentAttributes
        // ...restProps
    } = props;
    const [taxIdResults, setTaxIdResults] = React.useState<any>();
    const [isError, setIsError] = React.useState(false);
    const [idType, setIdType] = React.useState<any>('');
    const [taxId, setTaxId] = React.useState<any>('');
    const [paymentTypeId, setPaymentTypeId] = React.useState<any>('');
    const [isVisible, setIsVisible] = React.useState<any>(false);
    const [userCountry, setUserCountry] = React.useState<any>();
    const [hasRefreshed, setHasRefreshed] = React.useState<any>(false);
    const [updateTaxId, setUpdateTaxId] = React.useState<any>(false);

    const waitForUpdateFunction = async () => {
        await updateFunction();
    };

    const idTypeInputStyle: React.CSSProperties = {
        width: '100%',
        border: isError ? '1px solid red' : '1px solid rgb(135, 138, 140)',
        boxSizing: 'border-box',
        borderRadius: '2px',
        margin: '8px 0px',
        lineHeight: '22px',
        padding: '8px',
        fontSize: '16px',
        fontFamily: `"Ensign:Sans",Arial,"noto sans" ,sans-serif`,
        fontStyle: 'normal'
    };

    const idTypeStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        marginTop: '15px'
    };

    const validateBoleto = (rut: any) => {
        // Convert the RUT to a string and remove any non-numeric characters except for the check digit
        const rutDigits = rut.slice(0, -1).replace(/\D/g, '');
        const checkDigit = rut.slice(-1).toUpperCase(); // The last character is the check digit

        // Apply weights starting from 2 to 7, repeating as necessary
        let sum = 0;
        let weight = 2;

        // Loop through the digits of the RUT from right to left
        for (let i = rutDigits.length - 1; i >= 0; i--) {
            // eslint-disable-next-line radix, security/detect-object-injection
            sum += parseInt(rutDigits[i]) * weight;
            weight++;
            if (weight > 7) {
                weight = 2; // Reset weight after reaching 7
            }
        }

        // Calculate the remainder
        const remainder = sum % 11;
        let calculatedCheckDigit: any = 11 - remainder;

        // Handle special cases for the check digit
        if (calculatedCheckDigit === 11) {
            calculatedCheckDigit = '0'; // Check digit is '0' if result is 11
        } else if (calculatedCheckDigit === 10) {
            calculatedCheckDigit = 'K'; // Check digit is 'K' if result is 10
        } else {
            calculatedCheckDigit = calculatedCheckDigit.toString(); // Otherwise, it's a regular digit
        }

        // Return true if the calculated check digit matches the provided one, otherwise false
        return calculatedCheckDigit === checkDigit;
    };

    const validateCPF = (cpf: any) => {
        // Remove non-digit characters
        cpf = cpf.replace(/[^\d]/g, '');

        // Check if CPF length is 11 characters
        if (cpf.length !== 11) {
            return false;
        }

        // Check for known invalid CPF numbers
        if (
            cpf === '00000000000' ||
            cpf === '11111111111' ||
            cpf === '22222222222' ||
            cpf === '33333333333' ||
            cpf === '44444444444' ||
            cpf === '55555555555' ||
            cpf === '66666666666' ||
            cpf === '77777777777' ||
            cpf === '88888888888' ||
            cpf === '99999999999'
        ) {
            return false;
        }

        // Validate CPF algorithm
        let sum = 0;
        let remainder;

        for (let i = 1; i <= 9; i++) {
            sum += parseInt(cpf.substring(i - 1, i), 10) * (11 - i);
        }
        remainder = (sum * 10) % 11;

        if (remainder === 10 || remainder === 11) {
            remainder = 0;
        }

        if (remainder !== parseInt(cpf.substring(9, 10), 10)) {
            return false;
        }

        sum = 0;
        for (let i = 1; i <= 10; i++) {
            sum += parseInt(cpf.substring(i - 1, i), 10) * (12 - i);
        }
        remainder = (sum * 10) % 11;

        if (remainder === 10 || remainder === 11) {
            remainder = 0;
        }

        if (remainder !== parseInt(cpf.substring(10, 11), 10)) {
            return false;
        }

        return true;
    };

    const handleSelect = async (option: any) => {
        const selectedOption = taxIdResults.filter((taxId: any) => {
            return taxId.TypeId === option;
        });
        if (selectedOption && selectedOption[0]) {
            sessionStorage.setItem('TaxId', selectedOption[0].TypeId);
            if (selectedOption[0].Description.toLowerCase().includes('boleta')) {
                setUpdateTaxId(true);
            } else {
                setUpdateTaxId(false);
            }
        }

        setIdType(selectedOption[0].TypeId);
    };

    const onInputChange = (value: any) => {
        if (isError === true) {
            setIsError(false);
        }
        sessionStorage.setItem('idNum', value);
        setTaxId(value);
    };

    const getTaxIdInfo = async () => {
        const ctx = { callerContext: context.actionContext };
        let idTypes;
        // @ts-ignore
        if (props.data.cart && props.data.cart.result) {
            const getUserCountry: any =
                // @ts-ignore
                props.data.cart && props.data.cart.result && props.data.cart.result.cart && props.data.cart.result.cart.ExtensionProperties
                    ? // @ts-ignore
                      props.data.cart.result.cart.ExtensionProperties.filter((property: any) => {
                          return property.Key === 'Country';
                      })
                    : '';
            setUserCountry(getUserCountry);
            sessionStorage.setItem('taxIdRegion', getUserCountry[0].Value.StringValue);

            idTypes = await getIdentificationTypesAsync(ctx, getUserCountry[0].Value.StringValue).catch((err: any) => {
                return console.log(err);
            });
            if (idTypes && idTypes[0]) {
                sessionStorage.setItem('taxIdCheck', 'true');
            } else {
                sessionStorage.setItem('taxIdCheck', 'false');
                setIsVisible(true);
            }
        }

        return setTaxIdResults(idTypes);
    };
    React.useEffect(() => {
        const sessionTaxId = sessionStorage.getItem('TaxId');
        const sessionIdNum = sessionStorage.getItem('idNum');
        if (sessionTaxId) {
            setIdType(sessionTaxId);
            if (sessionTaxId.toLocaleLowerCase().includes('boleta')) {
                setUpdateTaxId(true);
            }
        }
        if (sessionIdNum) {
            setTaxId(sessionIdNum);
        }
        // void waitForUpdateFunction();
        void getTaxIdInfo();
    }, []);

    React.useEffect(() => {
        const paymentType = sessionStorage.getItem('paymentTypeId');

        if (paymentType === '7' && hasRefreshed === false && props.isVisible === true) {
            setHasRefreshed(true);
            void waitForUpdateFunction();
        }

        if (paymentType) {
            setPaymentTypeId(paymentType);
        }
    }, [props]);

    const insertDash = (str: string) => {
        if (str.includes('-')) {
            return str;
        } else {
            return `${str.slice(0, -1)}-${str.slice(-1)}`;
        }
    };

    const onClickCustom = async () => {
        const ctx = { callerContext: context.actionContext };
        let newTaxId: string = taxId;
        const userCountryRegion = sessionStorage.getItem('taxIdRegion');

        if (updateTaxId === true && userCountryRegion && userCountryRegion === 'CHL') {
            newTaxId = insertDash(taxId);
            setTaxId(newTaxId);
        }
        let validNum;
        if (userCountry[0].Value.StringValue === 'BRA' && idType === 'CPF') {
            validNum = validateCPF(newTaxId);
            if (validNum === true) {
                setIsVisible(true);
                if (isError === true) {
                    setIsError(false);
                }
            } else {
                setIsError(true);
            }

            if (validNum === true) {
                await updatePaymentAttributes(idType, newTaxId).then(() => {
                    setIsVisible(true);
                });
            }
        } else if (userCountry[0].Value.StringValue === 'CHL') {
            setTaxId(newTaxId.toLocaleUpperCase());
            validNum = validateBoleto(newTaxId);
            if (validNum === true) {
                setIsVisible(true);
                if (isError === true) {
                    setIsError(false);
                }
            } else {
                setIsError(true);
            }

            if (validNum === true) {
                await updatePaymentAttributes(idType, newTaxId).then(() => {
                    setIsVisible(true);
                });
            }
        } else {
            const taxIdCheck = await getIdentificationTypesApplicabilityRulesAsync(
                ctx,
                userCountry[0].Value.StringValue,
                idType,
                newTaxId
            ).catch(err => {
                console.log(err);
                setIsError(true);
            });
            if (taxIdCheck) {
                await updatePaymentAttributes(idType, newTaxId).then(() => {
                    setIsVisible(true);
                });
            }
        }
    };
    if (props.config.sensePassWithCreditCard && props.config.sensePassWithCreditCard === true) {
        if (paymentTypeId && paymentTypeId === '103' && props.id.includes('lds-checkout-payment-instrument__0')) {
            return (
                <Module {...checkoutPaymentInstrument}>
                    {taxIdResults && taxIdResults[0] ? (
                        <div style={idTypeStyle}>
                            <label>{resources.taxIdLabel}</label>
                            <select style={idTypeInputStyle} onChange={(e: any) => handleSelect(e.target.value)} disabled={isVisible}>
                                <option value='' disabled selected>
                                    {resources.taxIdPlaceholder}
                                </option>
                                {taxIdResults && taxIdResults[0]
                                    ? taxIdResults.map((taxId: any) => {
                                          return (
                                              <option value={taxId.TypeId} key={taxId.Id} selected={idType === taxId.TypeId}>
                                                  {taxId.Description}
                                              </option>
                                          );
                                      })
                                    : null}
                            </select>
                            <label>{resources.taxIdInputLabel}</label>
                            <input
                                type='text'
                                style={idTypeInputStyle}
                                onChange={(e: any) => onInputChange(e.target.value)}
                                value={taxId}
                                disabled={isVisible}
                            />
                            {isError && <small>{resources.taxIdErrorMessage}</small>}
                            {isVisible === false && <Secondary onClick={() => onClickCustom()}>{resources.applyPaymentLabel}</Secondary>}
                        </div>
                    ) : null}
                    {isVisible && <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>{waiting}</div>}
                    {/* @ts-ignore */}
                    {alert && alert.props.message && <Error>{alert.props.message}</Error>}
                    {paymentInformation}
                    {isVisible && addPaymentForm}
                    {overlayModal && <OverlayModal {...overlayModal} />}
                </Module>
            );
        } else if (paymentTypeId && paymentTypeId !== '103' && !props.id.includes('lds-checkout-payment-instrument__0')) {
            return (
                <Module {...checkoutPaymentInstrument}>
                    {taxIdResults && taxIdResults[0] ? (
                        <div style={idTypeStyle}>
                            <label>{resources.taxIdLabel}</label>
                            <select style={idTypeInputStyle} onChange={(e: any) => handleSelect(e.target.value)} disabled={isVisible}>
                                <option value='' disabled selected>
                                    {resources.taxIdPlaceholder}
                                </option>
                                {taxIdResults && taxIdResults.length !== 0 && taxIdResults[0]
                                    ? taxIdResults.map((taxId: any) => {
                                          return (
                                              <option value={taxId.TypeId} key={taxId.Id} selected={idType === taxId.TypeId}>
                                                  {taxId.Description}
                                              </option>
                                          );
                                      })
                                    : null}
                            </select>
                            <label>{resources.taxIdInputLabel}</label>
                            <input
                                type='text'
                                style={idTypeInputStyle}
                                onChange={(e: any) => onInputChange(e.target.value)}
                                value={taxId}
                                disabled={isVisible}
                            />
                            {isError && <small>{resources.taxIdErrorMessage}</small>}
                            {isVisible === false && <Secondary onClick={() => onClickCustom()}>{resources.applyPaymentLabel}</Secondary>}
                        </div>
                    ) : null}
                    {isVisible && <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>{waiting}</div>}
                    {/* @ts-ignore */}
                    {alert && alert.props.message && <Error>{alert.props.message}</Error>}
                    {paymentInformation}
                    {isVisible && addPaymentForm}
                    {overlayModal && <OverlayModal {...overlayModal} />}
                </Module>
            );
        } else {
            return null;
        }
    } else {
        return (
            <Module {...checkoutPaymentInstrument}>
                {taxIdResults && taxIdResults.length !== 0 && taxIdResults[0] ? (
                    <div style={idTypeStyle}>
                        <label>{resources.taxIdLabel}</label>
                        <select style={idTypeInputStyle} onChange={(e: any) => handleSelect(e.target.value)} disabled={isVisible}>
                            <option value='' disabled selected>
                                {resources.taxIdPlaceholder}
                            </option>
                            {taxIdResults && taxIdResults[0]
                                ? taxIdResults.map((taxId: any) => {
                                      return (
                                          <option value={taxId.TypeId} key={taxId.Id} selected={idType === taxId.TypeId}>
                                              {taxId.Description}
                                          </option>
                                      );
                                  })
                                : null}
                        </select>
                        <label>{resources.taxIdInputLabel}</label>
                        <input
                            type='text'
                            style={idTypeInputStyle}
                            onChange={(e: any) => onInputChange(e.target.value)}
                            value={taxId}
                            disabled={isVisible}
                        />
                        {isError && <small>{resources.taxIdErrorMessage}</small>}
                        {isVisible === false && <Secondary onClick={() => onClickCustom()}>{resources.applyPaymentLabel}</Secondary>}
                    </div>
                ) : null}
                {isVisible && <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>{waiting}</div>}
                {/* @ts-ignore */}
                {alert && alert.props.message && <Error>{alert.props.message}</Error>}
                {paymentInformation}
                {isVisible && addPaymentForm}
                {overlayModal && <OverlayModal {...overlayModal} />}
            </Module>
        );
    }
};

export default CheckoutPaymentInstrumentView;
